<template>
  <v-container fluid>
    <div class="d-flex align-center mb-4">
      <h1 class="text-h4">Recepciones</h1>
      <v-spacer></v-spacer>
      <!-- Flujo GS1 (checklist 4.5): escanear el SSCC (00) del pallet trae la recepción esperada -->
      <v-text-field
        v-model="ssccScan"
        label="Escanear SSCC (00) de la unidad logística"
        prepend-inner-icon="mdi-barcode-scan"
        density="compact" hide-details clearable autofocus
        style="max-width:360px" class="mr-4"
        @keyup.enter="onScanSSCC"
      ></v-text-field>
      <v-btn v-if="canCreate" color="primary" prepend-icon="mdi-plus" data-tour="recepcion-nueva" @click="openNew">Nueva Recepción</v-btn>
    </div>

    <v-alert v-if="scanMessage" :type="scanMessage.type" variant="tonal" density="compact" class="mb-3" closable @click:close="scanMessage = null">
      {{ scanMessage.text }}
    </v-alert>

    <v-card>
      <v-data-table :headers="headers" :items="receptions" :loading="loading" density="comfortable">
        <template v-slot:item.expected_ref="{ item }">
          {{ item.expected_ref }}
          <v-chip v-if="item.expected_source === 'dte'" size="x-small" color="indigo" variant="tonal" class="ml-1" title="Recepción creada desde el documento electrónico del proveedor">DTE</v-chip>
        </template>
        <template v-slot:item.reception_status="{ item }">
          <v-chip :color="statusColor(item.reception_status)" size="small">{{ statusText(item.reception_status) }}</v-chip>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-btn
            icon="mdi-clipboard-list-outline"
            size="small"
            variant="text"
            color="teal"
            @click="downloadReceptionPackingList(item)"
            title="Packing List de Recepción (ENTRADA) — claves GS1 del documento que entra"
          ></v-btn>
          <v-btn v-if="['expected','in_reception'].includes(item.reception_status)" size="small" variant="text" color="primary" @click="openCount(item)">Contar</v-btn>
          <v-btn v-if="item.reception_status==='pending_approval' && canApprove" size="small" variant="text" color="warning" @click="openDifferences(item)">Diferencias</v-btn>
          <v-chip v-if="item.reception_status==='pending_approval' && !canApprove" size="small" variant="tonal" color="warning">Esperando aprobación del jefe</v-chip>
          <!-- Recepción conforme (NGT 247 §7.1 d): fecha + responsable, antes de usar/implantar -->
          <v-btn v-if="['completed','approved'].includes(item.reception_status) && !item.fecha_recepcion_conforme && canApprove"
                 size="small" variant="text" color="teal-darken-2" prepend-icon="mdi-check-decagram"
                 @click="markConforme(item)">Conforme</v-btn>
          <v-chip v-if="item.fecha_recepcion_conforme" size="small" variant="tonal" color="teal-darken-2"
                  :title="'Recepción conforme por ' + (item.recepcion_conforme_por || '') + ' el ' + formatDateTime(item.fecha_recepcion_conforme)">
            <v-icon start size="small">mdi-check-decagram</v-icon>Conforme
          </v-chip>
          <!-- Series en diferido (NT 226): la recepción cerró con clave agrupadora -->
          <v-btn v-if="seriesPendientes(item) > 0" size="small" variant="text" color="orange-darken-2" prepend-icon="mdi-barcode"
                 @click="openSeries(item)">{{ seriesPendientes(item) }} series</v-btn>
          <!-- Putaway: cerrada la recepción, el jefe solicita el almacenamiento -->
          <v-btn v-if="['completed','approved'].includes(item.reception_status) && !item.storage_list && canApprove"
                 size="small" variant="text" color="teal" prepend-icon="mdi-warehouse"
                 @click="requestStorage(item)">Almacenar</v-btn>
          <v-chip v-if="item.storage_list" size="small" variant="tonal"
                  :color="item.storage_list.status === 'completed' ? 'success' : 'teal'">
            {{ item.storage_list.list_number }} · {{ storageStatusText(item.storage_list.status) }}{{ item.storage_list.assigned_user ? ' · ' + item.storage_list.assigned_user : '' }}
          </v-chip>
        </template>
        <template v-slot:no-data>No hay recepciones registradas</template>
      </v-data-table>
    </v-card>

    <!-- Modal: Nueva Recepción (JEFE) -->
    <v-dialog v-model="newDialog" max-width="900" persistent>
      <v-card>
        <v-card-title>Nueva Recepción (esperado)</v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12" md="4">
              <v-autocomplete
                v-model="form.supplier_id"
                :items="suppliers"
                item-title="razon_social"
                item-value="id"
                label="Proveedor (maestro)"
                :hint="selectedSupplier ? `RUT ${selectedSupplier.rut}` : 'Escribe para buscar; si no existe, créalo con +'"
                persistent-hint
                clearable
                @update:model-value="onSupplierPicked"
              >
                <template v-slot:item="{ props: itemProps, item }">
                  <v-list-item v-bind="itemProps" :subtitle="item.raw.rut + (item.raw.bloqueado ? ' · BLOQUEADO' : '')" :disabled="item.raw.bloqueado"></v-list-item>
                </template>
                <template v-slot:append>
                  <v-btn icon="mdi-plus" size="small" variant="text" title="Nuevo proveedor" @click="supplierDialog = true"></v-btn>
                </template>
              </v-autocomplete>
            </v-col>
            <v-col cols="12" md="4"><v-text-field v-model="form.expected_ref" :label="form.es_consignacion ? 'N° guía de consignación (sin factura)' : 'N° Guía / Factura'"></v-text-field></v-col>
            <v-col cols="12" md="4">
              <v-select v-model="form.destination_location_id" :items="locations" item-title="code" item-value="id" label="Ubicación destino"></v-select>
            </v-col>
            <v-col cols="12" v-if="sectorSalud">
              <v-switch v-model="form.es_consignacion" color="deep-purple" density="compact" hide-details data-tour="recepcion-consignacion"
                label="Stock en consignación: entra sin factura, sigue siendo del proveedor y se le avisa lo que se consume (forma de adquisición = consignación)"></v-switch>
            </v-col>
          </v-row>

          <!-- Importar Packing List GS1 (sin digitación — checklist GS1 4.5) -->
          <v-alert type="success" variant="tonal" density="compact" class="mb-3">
            <div class="d-flex align-center">
              <span>Importar <b>Packing List GS1</b> (JSON): crea el esperado automáticamente mapeando por GTIN, sin digitación.</span>
              <v-spacer></v-spacer>
              <v-file-input v-model="plFile" label="Packing List (.json)" accept=".json,application/json" density="compact" hide-details style="max-width:260px" prepend-icon="mdi-barcode-scan" :loading="importing" @update:modelValue="importPackingList"></v-file-input>
            </div>
          </v-alert>

          <!-- Zona de carga de PDF (guía/factura, incl. invoice extranjero) -->
          <v-alert type="info" variant="tonal" density="compact" class="mb-3">
            <div class="d-flex align-center">
              <span>Cargar guía / factura (PDF). La extracción automática con IA llegará pronto; por ahora carga las líneas a mano.</span>
              <v-spacer></v-spacer>
              <v-file-input v-model="form.pdf" label="PDF" accept="application/pdf" density="compact" hide-details style="max-width:240px" prepend-icon="mdi-file-pdf-box"></v-file-input>
            </div>
          </v-alert>

          <div class="d-flex align-center mb-2">
            <span class="text-subtitle-1">Líneas esperadas</span>
            <v-spacer></v-spacer>
            <v-btn size="small" variant="text" prepend-icon="mdi-plus" @click="form.lines.push({ product_id:null, expected_quantity:null })">Agregar línea</v-btn>
          </div>
          <v-table density="compact">
            <thead><tr><th>Producto</th><th style="width:160px">Cantidad esperada</th><th></th></tr></thead>
            <tbody>
              <tr v-for="(ln,i) in form.lines" :key="i">
                <td><v-select v-model="ln.product_id" :items="products" item-title="name" item-value="id" density="compact" hide-details></v-select></td>
                <td><v-text-field v-model.number="ln.expected_quantity" type="number" min="0" density="compact" hide-details></v-text-field></td>
                <td><v-btn icon="mdi-delete" size="x-small" variant="text" color="error" @click="form.lines.splice(i,1)"></v-btn></td>
              </tr>
            </tbody>
          </v-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="newDialog=false">Cancelar</v-btn>
          <v-btn color="primary" :disabled="!canSaveNew" @click="saveNew">Crear</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Modal: Conteo CIEGO (OPERARIO) -->
    <v-dialog v-model="countDialog" max-width="800" persistent>
      <v-card>
        <v-card-title>Conteo — {{ active && active.reference }}</v-card-title>
        <v-card-subtitle>Ingresa lo que realmente recibiste. No se muestra la cantidad esperada.</v-card-subtitle>
        <v-card-text>
          <!-- Conteo con escáner GS1: cada lectura identifica el producto por (01)/(02)
               y suma cantidad; lote/vencimiento/serie se llenan desde el código -->
          <v-text-field
            v-model="countScan"
            label="Escanear producto — GS1-128 / DataMatrix / EAN"
            prepend-inner-icon="mdi-barcode-scan"
            density="compact" hide-details clearable autofocus class="mb-3"
            @keyup.enter="onScanCount"
          ></v-text-field>
          <v-alert v-if="countMessage" :type="countMessage.type" variant="tonal" density="compact" class="mb-3">
            {{ countMessage.text }}
            <ul v-if="countMessage.details" class="mt-1 mb-0"><li v-for="d in countMessage.details" :key="d">{{ d }}</li></ul>
          </v-alert>
          <v-table density="compact">
            <thead><tr><th>Producto</th><th style="width:130px">Cant. recibida</th><th style="width:130px">Lote</th><th style="width:150px">Vencimiento</th><th style="width:130px">S/N</th></tr></thead>
            <tbody>
              <template v-for="ln in countLines" :key="ln.id">
                <tr :class="ln.id === lastScannedLineId ? 'bg-green-lighten-5' : ''">
                  <td>
                    {{ ln.product && ln.product.name }}
                    <v-chip v-if="isDm(ln)" size="x-small" color="teal" variant="tonal" class="ml-1">DM</v-chip>
                    <v-chip v-if="ln.product && ln.product.tipo_dm === 'implantable'" size="x-small" color="red" variant="tonal" class="ml-1">Implantable</v-chip>
                  </td>
                  <td><v-text-field v-model.number="ln.received_quantity" type="number" min="0" density="compact" hide-details></v-text-field></td>
                  <td><v-text-field v-model="ln.lot_number" density="compact" maxlength="20" :rules="gs1Max20Rules" hide-details="auto"></v-text-field></td>
                  <td>
                    <v-text-field v-model="ln.expiry_date" type="date" density="compact" hide-details
                      :hint="isDm(ln) && !ln.expiry_date && ln.product.politica_vencimiento === 'fecha_sustituta' ? 'Sin vencimiento: se aplicará la fecha sustituta' : ''"
                      :persistent-hint="isDm(ln) && !ln.expiry_date && ln.product.politica_vencimiento === 'fecha_sustituta'"></v-text-field>
                  </td>
                  <td><v-text-field v-model="ln.serial_number" density="compact" maxlength="20" :rules="gs1Max20Rules" hide-details="auto"
                      :placeholder="ln.product && ln.product.requiere_serie ? 'o registrar después' : ''"></v-text-field></td>
                </tr>
                <!-- Datos mínimos NGT 247 §7.1 (b, c, e) solo para dispositivos médicos -->
                <tr v-if="isDm(ln)" class="dm-row">
                  <td class="text-caption text-teal">Trazabilidad DM</td>
                  <td colspan="2">
                    <v-select v-model="ln.forma_adquisicion" :items="dmCatalogos.formas_adquisicion" item-title="label" item-value="value"
                      label="Forma de adquisición" density="compact" hide-details clearable></v-select>
                  </td>
                  <td>
                    <v-text-field v-model="ln.numero_inventario_activo_fijo" label="N° inventario" density="compact" maxlength="50" hide-details clearable></v-text-field>
                  </td>
                  <td>
                    <v-text-field v-model="ln.numero_registro_sanitario" label="Reg. ISP (lote)" density="compact" maxlength="50" hide-details clearable
                      :placeholder="ln.product.numero_registro_sanitario || ''"></v-text-field>
                  </td>
                </tr>
              </template>
            </tbody>
          </v-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="countDialog=false">Cerrar</v-btn>
          <v-btn variant="text" color="primary" @click="saveCount">Guardar conteo</v-btn>
          <v-btn color="success" @click="closeReception">Cerrar recepción</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Modal: Diferencias / Aprobar (JEFE) -->
    <v-dialog v-model="diffDialog" max-width="800" persistent>
      <v-card>
        <v-card-title>Diferencias — {{ active && active.reference }}</v-card-title>
        <v-card-text>
          <v-table density="compact">
            <thead><tr><th>Producto</th><th>Esperado</th><th>Recibido</th><th>Diferencia</th><th>Estado</th></tr></thead>
            <tbody>
              <tr v-for="d in differences" :key="d.id">
                <td>{{ d.product && d.product.name }}</td>
                <td>{{ d.expected_quantity }}</td>
                <td>{{ d.received_quantity }}</td>
                <td :class="d.difference>0 ? 'text-warning' : 'text-error'">{{ d.difference>0?'+':'' }}{{ d.difference }}</td>
                <td><v-chip size="x-small" :color="d.line_status==='quarantined'?'warning':'error'">{{ lineStatusText(d.line_status) }}</v-chip></td>
              </tr>
            </tbody>
          </v-table>
          <v-alert type="warning" variant="tonal" density="compact" class="mt-3">El sobrante está en cuarentena (no disponible). Al liberar pasa a stock disponible.</v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="diffDialog=false">Cerrar</v-btn>
          <v-btn variant="text" color="error" @click="approve('reject')">Rechazar sobrante</v-btn>
          <v-btn color="success" @click="approve('release')">Aprobar y liberar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- Modal: Asignar almacenamiento a operario (JEFE) -->
    <!-- Modal: Nuevo proveedor (alta rápida desde la recepción) -->
    <v-dialog v-model="supplierDialog" max-width="560" persistent>
      <v-card>
        <v-card-title>Nuevo proveedor</v-card-title>
        <v-card-text>
          <v-text-field v-model="supplierForm.rut" label="RUT *" placeholder="76.272.487-1" :rules="[v => !!v || 'Requerido']"></v-text-field>
          <v-text-field v-model="supplierForm.razon_social" label="Razón social *" :rules="[v => !!v || 'Requerido']"></v-text-field>
          <v-text-field v-model="supplierForm.email" label="Correo (avisos / casilla DTE)"></v-text-field>
          <v-text-field v-model="supplierForm.contacto" label="Contacto"></v-text-field>
          <v-alert v-if="supplierError" type="error" variant="tonal" density="compact">{{ supplierError }}</v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="supplierDialog = false">Cancelar</v-btn>
          <v-btn color="primary" :loading="savingSupplier" @click="saveSupplier">Crear</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Modal: registro diferido de series (NT 226 §IX.c) -->
    <v-dialog v-model="seriesDialog" max-width="720" persistent>
      <v-card>
        <v-card-title>Series pendientes — {{ seriesReception && seriesReception.reference }}</v-card-title>
        <v-card-subtitle>La recepción se cerró con clave agrupadora; escanea o digita cada número de serie (uno por línea).</v-card-subtitle>
        <v-card-text>
          <v-select v-model="seriesItemId" :items="seriesItems" item-title="label" item-value="id" label="Línea" density="compact" @update:model-value="loadSeries"></v-select>
          <div v-if="seriesInfo" class="text-caption mb-2">
            Clave agrupadora: <strong>{{ seriesInfo.clave_agrupadora_series }}</strong> · Cantidad: {{ seriesInfo.cantidad }} ·
            Registradas: {{ seriesInfo.series.length }} · <span :class="seriesInfo.series_pendientes ? 'text-orange-darken-2' : 'text-success'">Pendientes: {{ seriesInfo.series_pendientes }}</span>
          </div>
          <v-textarea v-model="seriesInput" label="Series (una por línea; acepta escaneos GS1 con (21))" rows="4" density="compact" auto-grow></v-textarea>
          <v-alert v-if="seriesMessage" :type="seriesMessage.type" variant="tonal" density="compact">{{ seriesMessage.text }}</v-alert>
          <v-chip-group v-if="seriesInfo && seriesInfo.series.length" column class="mt-2">
            <v-chip v-for="sn in seriesInfo.series" :key="sn.id" size="small" variant="tonal">{{ sn.serial_number }}</v-chip>
          </v-chip-group>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="seriesDialog = false; load()">Cerrar</v-btn>
          <v-btn color="primary" :disabled="!seriesItemId || !seriesInput.trim()" :loading="savingSeries" @click="saveSeries">Registrar series</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="storageDialog" max-width="640" persistent>
      <v-card>
        <v-card-title>Almacenar — {{ storageReception && storageReception.reference }}</v-card-title>
        <v-card-subtitle>Asigna el almacenamiento a un operario según su carga actual.</v-card-subtitle>
        <v-card-text>
          <v-table density="compact">
            <thead><tr><th></th><th>Operario</th><th>Rol</th><th class="text-center">Tareas pendientes</th><th class="text-center">Completadas (7d)</th></tr></thead>
            <tbody>
              <tr v-for="op in operators" :key="op.id"
                  :class="selectedOperator === op.id ? 'bg-teal-lighten-5' : ''"
                  style="cursor:pointer" @click="selectedOperator = op.id">
                <td><v-radio :model-value="selectedOperator === op.id" @click.stop="selectedOperator = op.id"></v-radio></td>
                <td>{{ op.username }}</td>
                <td>{{ op.role === 'picker' ? 'Picker' : 'Operario' }}</td>
                <td class="text-center">{{ op.pending_tasks }}</td>
                <td class="text-center">{{ op.completed_last_7d }}</td>
              </tr>
              <tr v-if="operators.length === 0"><td colspan="5" class="text-center text-grey">No hay operarios de bodega registrados</td></tr>
            </tbody>
          </v-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="storageDialog=false">Cancelar</v-btn>
          <v-btn color="teal" :disabled="!selectedOperator" :loading="assigning" @click="confirmStorage">Asignar almacenamiento</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import apiClient from '@/api/index.js'
import { mapGetters } from 'vuex'
import { parseGS1Scan, extractSSCC, gtinMatches } from '@/utils/gs1Parse'

export default {
  name: 'RecepcionesView',
  data() {
    return {
      loading: false,
      // Lote (10) y serie (21) admiten máx. 20 caracteres (checklist GS1 1.5/1.18)
      gs1Max20Rules: [v => !v || String(v).length <= 20 || 'No puede superar 20 caracteres'],
      receptions: [],
      ssccScan: '',
      scanMessage: null,
      countScan: '',
      countMessage: null,
      lastScannedLineId: null,
      products: [],
      locations: [],
      headers: [
        { title: 'Referencia', key: 'reference' },
        { title: 'Proveedor', key: 'supplier' },
        { title: 'N° Guía', key: 'expected_ref' },
        { title: 'Estado', key: 'reception_status' },
        { title: 'Acciones', key: 'actions', sortable: false },
      ],
      newDialog: false,
      countDialog: false,
      diffDialog: false,
      storageDialog: false,
      storageReception: null,
      operators: [],
      selectedOperator: null,
      assigning: false,
      autoAssignEnabled: false,
      active: null,
      countLines: [],
      differences: [],
      form: { supplier: '', supplier_id: null, expected_ref: '', destination_location_id: null, pdf: null, lines: [{ product_id: null, expected_quantity: null }] },
      plFile: null,
      importing: false,
      // Trazabilidad DM (NT 226 / NGT 247)
      suppliers: [],
      supplierDialog: false,
      supplierForm: { rut: '', razon_social: '', email: '', contacto: '' },
      supplierError: null,
      savingSupplier: false,
      dmCatalogos: { formas_adquisicion: [] },
      seriesDialog: false,
      seriesReception: null,
      seriesItems: [],
      seriesItemId: null,
      seriesInfo: null,
      seriesInput: '',
      seriesMessage: null,
      savingSeries: false,
    }
  },
  computed: {
    ...mapGetters('auth', ['hasPermission']),
    ...mapGetters('edition', ['sectorSalud']),
    // Matriz de perfiles: el JEFE crea el esperado y aprueba diferencias;
    // el OPERARIO solo cuenta (ciego) y cierra. La UI refleja el backend.
    canCreate() {
      return !this.hasPermission || this.hasPermission('reception:create')
    },
    canApprove() {
      return !this.hasPermission || this.hasPermission('reception:approve')
    },
    canSaveNew() {
      return this.form.destination_location_id && this.form.lines.some(l => l.product_id && l.expected_quantity > 0)
    },
    selectedSupplier() {
      return this.suppliers.find(s => s.id === this.form.supplier_id) || null
    }
  },
  async mounted() {
    this.load(); this.loadRefs()
    // Config General: con asignación automática no se muestra el modal de operarios
    try {
      const { data } = await apiClient.get('/company/settings')
      this.autoAssignEnabled = !!data.auto_assign_enabled
    } catch (e) { /* noop */ }
  },
  methods: {
    // Packing List de Recepción (ENTRADA) — documento separado del de salida
    // (observación 1ª evaluación GS1) + visor del packing list que entra (4.5)
    async downloadReceptionPackingList(reception) {
      try {
        const response = await apiClient.get(`/receptions/${reception.id}/packing-list?format=pdf`, { responseType: 'blob' })
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.download = `packing_list_recepcion_${reception.reference}.pdf`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      } catch (e) {
        console.error('Error descargando packing list de recepción:', e)
        this.$store.dispatch('showError', 'Error generando el Packing List de Recepción')
      }
    },
    async load() {
      this.loading = true
      try { this.receptions = (await apiClient.get('/receptions')).data } finally { this.loading = false }
    },
    async loadRefs() {
      try {
        this.products = (await apiClient.get('/products')).data
        const locs = (await apiClient.get('/locations')).data
        this.locations = Array.isArray(locs) ? locs : (locs.data || [])
      } catch (e) { /* noop */ }
      try { this.suppliers = (await apiClient.get('/suppliers')).data } catch (e) { this.suppliers = [] }
      try { this.dmCatalogos = (await apiClient.get('/dm/catalogos')).data } catch (e) { /* noop */ }
    },
    // ===== Trazabilidad DM =====
    isDm(line) {
      return !!(line.product && line.product.es_dispositivo_medico)
    },
    onSupplierPicked(id) {
      const s = this.suppliers.find(x => x.id === id)
      this.form.supplier = s ? s.razon_social : ''
    },
    async saveSupplier() {
      this.supplierError = null
      this.savingSupplier = true
      try {
        const { data } = await apiClient.post('/suppliers', this.supplierForm)
        this.suppliers.push(data)
        this.form.supplier_id = data.id
        this.form.supplier = data.razon_social
        this.supplierDialog = false
        this.supplierForm = { rut: '', razon_social: '', email: '', contacto: '' }
      } catch (e) {
        this.supplierError = e.response?.data?.error || e.message
      } finally {
        this.savingSupplier = false
      }
    },
    formatDateTime(v) {
      if (!v) return ''
      const d = new Date(v)
      return isNaN(d) ? v : d.toLocaleString('es-CL')
    },
    seriesPendientes(reception) {
      return (reception.transaction_items || []).reduce((acc, it) => acc + (it.series_pendientes || 0), 0)
    },
    async markConforme(item) {
      if (!confirm(`¿Confirmas la recepción conforme de ${item.reference}? Quedará registrada con fecha, hora y tu usuario (NGT 247).`)) return
      try {
        await apiClient.post(`/receptions/${item.id}/conforme`, {})
        this.scanMessage = { type: 'success', text: `Recepción ${item.reference} marcada conforme.` }
        this.load()
      } catch (e) {
        const r = e.response?.data
        if (r?.details) {
          if (confirm(`${r.error}:\n${r.details.join('\n')}\n\n¿Marcar conforme de todas formas?`)) {
            await apiClient.post(`/receptions/${item.id}/conforme`, { force: true })
            this.load()
          }
        } else {
          this.scanMessage = { type: 'error', text: r?.error || e.message }
        }
      }
    },
    openSeries(item) {
      this.seriesReception = item
      this.seriesItems = (item.transaction_items || [])
        .filter(it => (it.series_pendientes || 0) > 0 || it.clave_agrupadora_series)
        .map(it => ({ id: it.id, label: `${it.product?.name || 'Producto'} — pendientes: ${it.series_pendientes || 0}` }))
      this.seriesItemId = this.seriesItems.length ? this.seriesItems[0].id : null
      this.seriesInput = ''
      this.seriesMessage = null
      this.seriesInfo = null
      this.seriesDialog = true
      if (this.seriesItemId) this.loadSeries()
    },
    async loadSeries() {
      if (!this.seriesItemId) return
      this.seriesInfo = (await apiClient.get(`/receptions/${this.seriesReception.id}/items/${this.seriesItemId}/series`)).data
    },
    async saveSeries() {
      // Cada línea puede ser una serie plana o un escaneo GS1: se extrae el (21)
      const serials = this.seriesInput.split(/\r?\n/).map(x => x.trim()).filter(Boolean).map(x => {
        try { const p = parseGS1Scan(x); return p.serial || x } catch (e) { return x }
      })
      this.savingSeries = true
      try {
        const { data } = await apiClient.post(`/receptions/${this.seriesReception.id}/items/${this.seriesItemId}/series`, { serials })
        this.seriesMessage = { type: 'success', text: `${data.message}${data.duplicates?.length ? ' · duplicadas: ' + data.duplicates.join(', ') : ''} · pendientes: ${data.series_pendientes}` }
        this.seriesInput = ''
        await this.loadSeries()
      } catch (e) {
        this.seriesMessage = { type: 'error', text: e.response?.data?.error || e.message }
      } finally {
        this.savingSeries = false
      }
    },
    openNew() {
      this.form = { supplier: '', supplier_id: null, expected_ref: '', destination_location_id: null, pdf: null, es_consignacion: false, lines: [{ product_id: null, expected_quantity: null }] }
      this.newDialog = true
    },
    async saveNew() {
      const payload = {
        supplier: this.form.supplier, supplier_id: this.form.supplier_id, expected_ref: this.form.expected_ref,
        destination_location_id: this.form.destination_location_id, es_consignacion: !!this.form.es_consignacion,
        lines: this.form.lines.filter(l => l.product_id && l.expected_quantity > 0),
      }
      await apiClient.post('/receptions', payload)
      this.newDialog = false; this.load()
    },
    // Importar Packing List GS1 (JSON) → recepción esperada sin digitación
    async importPackingList(file) {
      const f = Array.isArray(file) ? file[0] : file
      if (!f) return
      this.importing = true
      try {
        const text = await f.text()
        const payload = JSON.parse(text)
        const { data } = await apiClient.post('/receptions/import-packing-list', payload)
        this.newDialog = false
        this.plFile = null
        await this.load()
        alert(`Packing List importado: recepción ${data.reference} con ${data.imported_lines} línea(s) esperada(s).`)
      } catch (e) {
        const resp = e.response?.data
        if (resp?.not_found) {
          alert(`No se pudo importar: hay GTINs sin producto en el sistema:\n${resp.not_found.map(n => `${n.gtin} — ${n.description || ''}`).join('\n')}`)
        } else {
          alert(`Error importando packing list: ${resp?.error || e.message}`)
        }
      } finally {
        this.importing = false
      }
    },
    // Flujo GS1 pasos 4-5: escanear (00)SSCC → validar → traer la recepción esperada
    onScanSSCC() {
      const code = this.ssccScan
      this.ssccScan = ''
      if (!code) return
      const { sscc, error } = extractSSCC(code)
      if (error) {
        this.scanMessage = { type: 'error', text: error }
        return
      }
      const rec = this.receptions.find(r => r.expected_ref === sscc)
      if (!rec) {
        this.scanMessage = { type: 'warning', text: `SSCC ${sscc} válido, pero no hay ninguna recepción esperada con esa unidad logística. Importá primero el Packing List.` }
        return
      }
      if (['expected', 'in_reception'].includes(rec.reception_status)) {
        this.scanMessage = { type: 'success', text: `SSCC ${sscc} validado → recepción ${rec.reference} (${rec.supplier || 'sin proveedor'}). Iniciando comprobación física.` }
        this.openCount(rec)
      } else if (rec.reception_status === 'pending_approval') {
        if (this.canApprove) {
          this.scanMessage = { type: 'info', text: `SSCC ${sscc} → recepción ${rec.reference} ya contada, con diferencias por aprobar.` }
          this.openDifferences(rec)
        } else {
          this.scanMessage = { type: 'info', text: `SSCC ${sscc} → recepción ${rec.reference} ya fue contada y espera aprobación del jefe de bodega.` }
        }
      } else {
        this.scanMessage = { type: 'info', text: `SSCC ${sscc} → recepción ${rec.reference} ya está ${this.statusText(rec.reception_status).toLowerCase()}.` }
      }
    },
    // Conteo ciego con escáner: (01)/(02) identifica la línea; (10)(17)(21)(37) llenan los campos
    onScanCount() {
      const code = this.countScan
      this.countScan = ''
      if (!code) return
      const p = parseGS1Scan(code)
      const gtin = p.gtin || p.content_gtin
      if (!gtin) {
        this.countMessage = { type: 'error', text: 'El código no contiene un GTIN (01)/(02) reconocible' }
        return
      }
      const line = this.countLines.find(l => {
        const prod = l.product || {}
        return gtinMatches(gtin, prod.gtin) || gtinMatches(gtin, prod.barcode)
      })
      if (!line) {
        this.countMessage = { type: 'warning', text: `GTIN ${gtin} no está entre los productos esperados de esta recepción` }
        return
      }
      // Item con serie = unidad única; si no, suma (37) o 1
      const qty = p.serial ? 1 : (p.count || 1)
      line.received_quantity = (line.received_quantity || 0) + qty
      if (p.lot) line.lot_number = p.lot
      if (p.expiry) line.expiry_date = p.expiry
      if (p.serial) line.serial_number = p.serial
      this.lastScannedLineId = line.id
      const extras = [p.lot && `lote ${p.lot}`, p.expiry && `venc ${p.expiry}`, p.serial && `S/N ${p.serial}`].filter(Boolean).join(', ')
      this.countMessage = { type: 'success', text: `${line.product?.name}: +${qty} (total ${line.received_quantity})${extras ? ' — ' + extras : ''}` }
    },
    async openCount(item) {
      this.active = item
      const data = (await apiClient.get(`/receptions/${item.id}/counting`)).data
      this.countLines = data.lines.map(l => ({
        ...l,
        received_quantity: l.received_quantity ?? null,
        expiry_date: l.expiry_date ? String(l.expiry_date).slice(0, 10) : null,
        forma_adquisicion: l.forma_adquisicion || null,
        numero_inventario_activo_fijo: l.numero_inventario_activo_fijo || '',
        numero_registro_sanitario: l.numero_registro_sanitario || '',
      }))
      this.countMessage = null
      this.lastScannedLineId = null
      this.countDialog = true
    },
    async saveCount() {
      const counts = this.countLines.map(l => ({
        line_id: l.id,
        received_quantity: l.received_quantity,
        lot_number: l.lot_number,
        expiry_date: l.expiry_date || null,
        serial_number: l.serial_number,
        forma_adquisicion: l.forma_adquisicion || null,
        numero_inventario_activo_fijo: l.numero_inventario_activo_fijo || null,
        numero_registro_sanitario: l.numero_registro_sanitario || null,
      }))
      await apiClient.post(`/receptions/${this.active.id}/count`, { counts })
    },
    async closeReception() {
      try {
        await this.saveCount()
        await apiClient.post(`/receptions/${this.active.id}/close`, {})
        this.countDialog = false; this.load()
      } catch (e) {
        const r = e.response?.data
        // 422: la recepción no cumple los datos mínimos NT 226 (lote/serie/vencimiento) — se muestra por línea
        this.countMessage = { type: 'error', text: r?.error || e.message, details: r?.details || null }
      }
    },
    async openDifferences(item) {
      this.active = item
      this.differences = (await apiClient.get(`/receptions/${item.id}/differences`)).data.differences
      this.diffDialog = true
    },
    async approve(action) {
      await apiClient.post(`/receptions/${this.active.id}/approve`, { action })
      this.diffDialog = false
      // Guiar el paso siguiente del flujo: el putaway lo gatilla el jefe
      this.scanMessage = {
        type: 'success',
        text: action === 'reject'
          ? `Sobrante de ${this.active.reference} rechazado. El resto del stock quedó en Zona de Recepción — usa "Almacenar" para asignar el almacenamiento a un operario.`
          : `Recepción ${this.active.reference} aprobada (sobrante liberado). El stock quedó en Zona de Recepción — usa "Almacenar" para asignar el almacenamiento a un operario.`,
      }
      this.load()
    },
    // ===== Putaway: solicitar almacenamiento de la recepción cerrada =====
    async requestStorage(item) {
      if (this.autoAssignEnabled) {
        // Asignación automática al operario más disponible (config General)
        try {
          const { data } = await apiClient.post(`/receptions/${item.id}/request-storage`, {})
          this.scanMessage = { type: 'success', text: `Almacenamiento ${data.list_number} asignado automáticamente a ${data.assigned_user || 'operario'}.` }
          this.load()
        } catch (e) {
          this.scanMessage = { type: 'error', text: e.response?.data?.error || 'Error creando el almacenamiento' }
        }
        return
      }
      this.storageReception = item
      this.selectedOperator = null
      try {
        this.operators = (await apiClient.get('/receptions/storage-operators')).data
      } catch (e) {
        this.operators = []
      }
      this.storageDialog = true
    },
    async confirmStorage() {
      this.assigning = true
      try {
        const { data } = await apiClient.post(`/receptions/${this.storageReception.id}/request-storage`, {
          assigned_user_id: this.selectedOperator,
        })
        this.storageDialog = false
        this.scanMessage = { type: 'success', text: `Almacenamiento ${data.list_number} asignado a ${data.assigned_user}. El operario lo verá en sus tareas.` }
        this.load()
      } catch (e) {
        this.scanMessage = { type: 'error', text: e.response?.data?.error || 'Error asignando el almacenamiento' }
      } finally {
        this.assigning = false
      }
    },
    storageStatusText(s) {
      return { pending: 'Por asignar', assigned: 'Asignado', in_progress: 'En proceso', completed: 'Almacenado' }[s] || s
    },
    statusColor(s) {
      return { expected: 'grey', in_reception: 'blue', pending_approval: 'warning', approved: 'success', completed: 'success', rejected: 'error' }[s] || 'grey'
    },
    statusText(s) {
      return { expected: 'Esperada', in_reception: 'En conteo', with_differences: 'Con diferencias', pending_approval: 'Por aprobar', approved: 'Aprobada', completed: 'Completada', rejected: 'Rechazada' }[s] || s
    },
    lineStatusText(s) {
      return { ok: 'OK', shortage: 'Faltante', overage: 'Sobrante', quarantined: 'Cuarentena' }[s] || s
    },
  },
}
</script>

<style scoped>
.dm-row td { background: #f4fbfa; border-bottom: 1px solid #b2dfdb !important; padding-top: 6px; padding-bottom: 6px; }
</style>
